import { useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/authContext";
import { fetchWithAuth } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { ShieldAlert, ChevronDown, Loader2, Upload, RefreshCw } from "lucide-react";

// ---- Tab config ----

type TabId = "accounts" | "templates" | "projects" | "client-notes" | "time-activities" | "users" | "project-roles" | "project-assignments" | "client-groups";

interface TabConfig {
  label: string;
  endpoint: string;
  description: string;
  fcSource: string;
  // How to extract the rows array from parsed JSON
  extractRows: (parsed: unknown) => unknown[] | null;
  // Build the POST body from the rows array + dry_run flag
  buildBody: (rows: unknown[], dryRun: boolean) => Record<string, unknown>;
  validate: (rows: unknown[]) => string | null;
  columns: ColumnDef[];
  exampleJson: string;
}

type ColumnDef = { header: string; accessor: (row: any) => string };

// ---- Tab definitions ----

const TABS: Record<TabId, TabConfig> = {
  accounts: {
    label: "Accounts",
    endpoint: "/api/admin/import/accounts",
    description: "Paste the raw JSON response from FC GET /clients. Contacts bundled in each client record are imported automatically.",
    fcSource: "GET /clients",
    extractRows: (p) => {
      if (Array.isArray(p)) return p;
      if (p && typeof p === "object" && Array.isArray((p as any).data)) return (p as any).data;
      return null;
    },
    buildBody: (rows, dryRun) => ({ data: rows, dry_run: dryRun }),
    validate: (rows) => {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] as any;
        if (!r.name) return `Row ${i + 1}: "name" is required (FC client field)`;
      }
      return null;
    },
    columns: [
      { header: "Name", accessor: (r) => r.name ?? "" },
      { header: "Active", accessor: (r) => (r.is_archived ? "No" : "Yes") },
      { header: "FC ID", accessor: (r) => String(r.id ?? "") },
      { header: "Contacts", accessor: (r) => String((r.contacts || []).length) },
    ],
    exampleJson: JSON.stringify(
      {
        data: [
          {
            id: 12345,
            name: "Smith & Associates LLC",
            is_archived: false,
            contacts: [
              { id: 9001, name: "Jane Smith", email: "jane@smithassoc.com", phone: "555-123-4567", is_primary: true },
            ],
          },
          {
            id: 12346,
            name: "Jones Contractors Inc",
            is_archived: false,
            contacts: [],
          },
        ],
        links: {},
        meta: {},
      },
      null,
      2
    ),
  },

  templates: {
    label: "Templates",
    endpoint: "/api/admin/import/templates",
    description: "Paste an array of FC GET /templates/{id} detail responses. Each must include tasks[] and client_tasks[].",
    fcSource: "GET /templates/{id} (array of detail responses)",
    extractRows: (p) => {
      if (Array.isArray(p)) return p;
      if (p && typeof p === "object" && Array.isArray((p as any).data)) return (p as any).data;
      return null;
    },
    buildBody: (rows, dryRun) => ({ data: rows, dry_run: dryRun }),
    validate: (rows) => {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] as any;
        if (!r.title) return `Row ${i + 1}: "title" is required`;
      }
      return null;
    },
    columns: [
      { header: "Title", accessor: (r) => r.title ?? "" },
      { header: "FC ID", accessor: (r) => String(r.id ?? "") },
      { header: "Firm Tasks", accessor: (r) => String((r.tasks || []).length) },
      { header: "Client Tasks", accessor: (r) => String((r.client_tasks || []).length) },
    ],
    exampleJson: JSON.stringify(
      [
        {
          id: 501,
          title: "Monthly Bookkeeping",
          recurring_pattern: "monthly",
          tasks: [
            { id: 1, title: "Reconcile accounts", section: { id: 10, name: "Firm Tasks", list_index: 0 }, list_index: 0 },
            { id: 2, title: "Prepare financials", section: { id: 10, name: "Firm Tasks", list_index: 0 }, list_index: 1 },
          ],
          client_tasks: [
            { id: 3, title: "Upload bank statements", section: { id: 11, name: "Client Tasks", list_index: 1 }, allow_client_upload: true, list_index: 0 },
          ],
        },
      ],
      null,
      2
    ),
  },

  projects: {
    label: "Projects",
    endpoint: "/api/admin/import/projects",
    description: "Paste the raw JSON response from FC GET /projects. Tasks are regenerated from the linked template on import.",
    fcSource: "GET /projects",
    extractRows: (p) => {
      if (Array.isArray(p)) return p;
      if (p && typeof p === "object" && Array.isArray((p as any).data)) return (p as any).data;
      return null;
    },
    buildBody: (rows, dryRun) => ({ data: rows, dry_run: dryRun }),
    validate: (rows) => {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] as any;
        if (!r.title) return `Row ${i + 1}: "title" is required`;
      }
      return null;
    },
    columns: [
      { header: "Title", accessor: (r) => r.title ?? "" },
      { header: "Client", accessor: (r) => r.client?.name ?? "" },
      { header: "Status", accessor: (r) => (r.is_closed ? "closed" : "open") },
      { header: "Due Date", accessor: (r) => r.due_date ?? "" },
      { header: "FC ID", accessor: (r) => String(r.id ?? "") },
    ],
    exampleJson: JSON.stringify(
      {
        data: [
          {
            id: 9001,
            title: "April 2026 Bookkeeping",
            client: { id: 12345, name: "Smith & Associates LLC" },
            is_closed: false,
            start_date: "2026-04-01",
            due_date: "2026-04-30",
            internal_due_date: "2026-04-25",
            template_id: 501,
            accounting_period: "monthly",
            accounting_period_date: "2026-04-30",
            computed_accounting_period: "April 2026",
            tags: [{ id: 59879, name: "Payroll" }],
          },
        ],
        links: {},
        meta: {},
      },
      null,
      2
    ),
  },

  "client-notes": {
    label: "Client Notes",
    endpoint: "/api/admin/import/client-notes",
    description: "Paste batched FC GET /clients/{id}/notes responses. Each batch entry must include fc_client_id and notes[].",
    fcSource: "GET /clients/{id}/notes (batched)",
    extractRows: (p) => {
      if (Array.isArray(p)) return p;
      if (p && typeof p === "object" && Array.isArray((p as any).data)) return (p as any).data;
      return null;
    },
    buildBody: (rows, dryRun) => ({ data: rows, dry_run: dryRun }),
    validate: (rows) => {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] as any;
        if (!r.fc_client_id) return `Row ${i + 1}: "fc_client_id" is required`;
        if (!Array.isArray(r.notes)) return `Row ${i + 1}: "notes" must be an array`;
      }
      return null;
    },
    columns: [
      { header: "FC Client ID", accessor: (r) => String(r.fc_client_id ?? "") },
      { header: "Notes Count", accessor: (r) => String((r.notes || []).length) },
      { header: "Pinned", accessor: (r) => String((r.notes || []).filter((n: any) => n.is_pinned).length) },
    ],
    exampleJson: JSON.stringify(
      {
        data: [
          {
            fc_client_id: "12345",
            notes: [
              {
                id: 7001,
                title: "Entity Info",
                body: "<p>LLC formed 2018, S-election effective 2019.</p>",
                plain_text: "LLC formed 2018, S-election effective 2019.",
                is_pinned: true,
              },
              {
                id: 7002,
                title: "Bank Info",
                body: "<p>First National Bank checking.</p>",
                plain_text: "First National Bank checking.",
                is_pinned: false,
              },
            ],
          },
        ],
      },
      null,
      2
    ),
  },

  "time-activities": {
    label: "Time Activities",
    endpoint: "/api/admin/import/time-activities",
    description: "Paste FC GET /time-activities paginated response. Only 2026-01-01 forward is imported.",
    fcSource: "GET /time-activities (paginated)",
    extractRows: (p) => {
      if (Array.isArray(p)) return p;
      if (p && typeof p === "object" && Array.isArray((p as any).data)) return (p as any).data;
      return null;
    },
    buildBody: (rows, dryRun) => ({ data: rows, dry_run: dryRun }),
    validate: (rows) => {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] as any;
        if (!r.id) return `Row ${i + 1}: "id" is required`;
      }
      return null;
    },
    columns: [
      { header: "FC ID", accessor: (r) => String(r.id ?? "") },
      { header: "Client", accessor: (r) => r.client?.name ?? "" },
      { header: "User", accessor: (r) => r.user?.name ?? r.user ?? "" },
      { header: "Date", accessor: (r) => r.activity_date ?? "" },
      { header: "Hours", accessor: (r) => String(r.hours ?? "") },
    ],
    exampleJson: JSON.stringify(
      {
        data: [
          {
            id: 50001,
            client: { id: 12345, name: "Smith & Associates LLC" },
            project: { id: 9001, title: "April 2026 Bookkeeping" },
            user: { name: "Wade Carpenter" },
            hours: 2.5,
            activity_date: "2026-04-10",
            comments: "Reconciled checking account",
            service_item: { name: "Bookkeeping" },
            log_type: "manual",
            billing_rate: 150.0,
            completed_at: "2026-04-10T18:00:00Z",
          },
        ],
        links: { next: "https://app.financial-cents.com/api/v1/time-activities?page=2" },
        meta: { current_page: 1, total: 1 },
      },
      null,
      2
    ),
  },

  users: {
    label: "Users",
    endpoint: "/api/admin/import/users",
    description: "Paste fc-users-export.json output. Matches FC team members to CommandHub users by email then name, and sets fc_user_id for precise linking. Run this BEFORE Project Assignments.",
    fcSource: "node scripts/fetch-fc-users.mjs → fc-users-export.json",
    extractRows: (p) => {
      if (Array.isArray(p)) return p;
      if (p && typeof p === "object" && Array.isArray((p as any).data)) return (p as any).data;
      return null;
    },
    buildBody: (rows, dryRun) => ({ data: rows, dry_run: dryRun }),
    validate: (rows) => {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] as any;
        if (!r.id) return `Row ${i + 1}: "id" is required`;
      }
      return null;
    },
    columns: [
      { header: "FC ID", accessor: (r) => String(r.id ?? "") },
      { header: "Name", accessor: (r) => r.name ?? "" },
      { header: "Email", accessor: (r) => r.email ?? "" },
    ],
    exampleJson: JSON.stringify({ data: [{ id: 456, name: "Wade Carpenter", email: "wade@ccpas.net" }] }, null, 2),
  },

  "project-roles": {
    label: "Project Roles",
    endpoint: "/api/admin/import/project-roles",
    description: "Paste fc-project-roles-export.json output. Seeds or updates project_roles table with FC role definitions (Preparer, Reviewer, etc.).",
    fcSource: "node scripts/fetch-fc-project-roles.mjs → fc-project-roles-export.json",
    extractRows: (p) => {
      if (Array.isArray(p)) return p;
      if (p && typeof p === "object" && Array.isArray((p as any).data)) return (p as any).data;
      return null;
    },
    buildBody: (rows, dryRun) => ({ data: rows, dry_run: dryRun }),
    validate: (rows) => {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] as any;
        if (!r.name) return `Row ${i + 1}: "name" is required`;
      }
      return null;
    },
    columns: [
      { header: "FC ID", accessor: (r) => String(r.id ?? "") },
      { header: "Name", accessor: (r) => r.name ?? "" },
    ],
    exampleJson: JSON.stringify({ data: [{ id: 1, name: "Preparer" }, { id: 2, name: "Reviewer" }] }, null, 2),
  },

  "project-assignments": {
    label: "Project Assignments",
    endpoint: "/api/admin/import/project-assignments",
    description: "Paste a FRESH fc-projects-export.json (re-run fetch-fc-projects.mjs). Populates project team members for all 3,729 imported projects. Requires Users import to have run first.",
    fcSource: "node scripts/fetch-fc-projects.mjs → fc-projects-export.json (re-fetch)",
    extractRows: (p) => {
      if (Array.isArray(p)) return p;
      if (p && typeof p === "object" && Array.isArray((p as any).data)) return (p as any).data;
      return null;
    },
    buildBody: (rows, dryRun) => ({ data: rows, dry_run: dryRun }),
    validate: (rows) => {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] as any;
        if (!r.id) return `Row ${i + 1}: "id" (fc_project_id) is required`;
      }
      return null;
    },
    columns: [
      { header: "FC Project ID", accessor: (r) => String(r.id ?? "") },
      { header: "Title", accessor: (r) => r.title ?? "" },
      { header: "Users count", accessor: (r) => String((r.users ?? r.assignees ?? r.team_members ?? []).length) },
    ],
    exampleJson: JSON.stringify({ data: [{ id: 12345, title: "April 2026 Payroll", users: [{ id: 456, name: "Wade Carpenter", email: "wade@ccpas.net", pivot: { role: "owner" } }] }] }, null, 2),
  },
  "client-groups": {
    label: "Client Groups",
    endpoint: "/api/admin/import/client-groups",
    description: "Paste one FC group JSON from browser devtools, or a JSON array of multiple groups. Creates client_groups + client_group_members rows and populates account_members for staff-owned groups.",
    fcSource: "Browser devtools — FC group detail page (not in public API)",
    extractRows: (parsed) => {
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const d = (parsed as any).data ?? parsed;
        if (d && Array.isArray(d.clients)) return [d]; // single group object — wrap in array
        if (Array.isArray(d)) return d;
      }
      if (Array.isArray(parsed)) return parsed;
      return null;
    },
    buildBody: (rows, dryRun) => ({ data: rows, dry_run: dryRun }),
    validate: (rows) => {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] as any;
        if (!r.name) return `Row ${i + 1}: missing "name"`;
        if (!Array.isArray(r.clients)) return `Row ${i + 1}: missing "clients" array`;
      }
      return null;
    },
    columns: [
      { header: "Group Name", accessor: (r) => r.name ?? "" },
      { header: "Owner", accessor: (r) => r.user?.name ?? "(no owner)" },
      { header: "Owner Email", accessor: (r) => r.user?.email ?? "" },
      { header: "Clients", accessor: (r) => String((r.clients || []).length) },
    ],
    exampleJson: JSON.stringify({ data: { id: 11153, name: "Amanda's Clients", user_id: 11242, user: { id: 11242, name: "Amanda Denton", email: "adenton@carpentercpas.com" }, clients: [{ id: 885130, name: "30155 Groesbeck, LLC", pivot: { group_id: 11153, client_id: 885130 } }] } }, null, 2),
  },
};

// ---- ImportResult type ----

interface ImportResult {
  created?: number;
  updated?: number;
  skipped?: number;
  matched?: number;
  filtered_pre_2026?: number;
  contacts_created?: number;
  projects_processed?: number;
  assignments_created?: number;
  assignments_skipped?: number;
  unmatched_users?: string[];
  unmatched_projects_count?: number;
  // client-groups
  groups_created?: number;
  groups_skipped?: number;
  members_created?: number;
  warnings?: { group: string; reason: string }[];
  errors: { row?: number; batch?: number; group?: string; reason: string }[];
}

// ---- About-fields types ----

interface AboutFieldsResult {
  updated: number;
  skipped: number;
  errors: { account_id: string; fc_client_id: string; reason: string }[];
}
interface AboutFieldsState { dryRun: boolean; isRunning: boolean; result: AboutFieldsResult | null; }

// ---- Panel state ----

interface PanelState {
  rawText: string;
  parsedRows: unknown[] | null;
  parseError: string | null;
  dryRun: boolean;
  isImporting: boolean;
  result: ImportResult | null;
}

const EMPTY_PANEL: PanelState = {
  rawText: "",
  parsedRows: null,
  parseError: null,
  dryRun: true,
  isImporting: false,
  result: null,
};

// ---- ImportPanel component ----

function ImportPanel({ tabId }: { tabId: TabId }) {
  const config = TABS[tabId];
  const [state, setState] = useState<PanelState>({ ...EMPTY_PANEL });
  const [exampleOpen, setExampleOpen] = useState(false);

  function handleValidate() {
    setState((s) => ({ ...s, parsedRows: null, parseError: null, result: null }));
    let parsed: unknown;
    try {
      parsed = JSON.parse(state.rawText);
    } catch {
      setState((s) => ({ ...s, parseError: "Invalid JSON — check syntax and try again." }));
      return;
    }

    const rows = config.extractRows(parsed);
    if (!rows) {
      setState((s) => ({ ...s, parseError: 'Expected a JSON array or an object with a "data" array.' }));
      return;
    }
    if (rows.length === 0) {
      setState((s) => ({ ...s, parseError: "Array is empty — nothing to import." }));
      return;
    }

    const validationError = config.validate(rows);
    if (validationError) {
      setState((s) => ({ ...s, parseError: validationError }));
      return;
    }

    setState((s) => ({ ...s, parsedRows: rows, parseError: null }));
  }

  async function handleImport() {
    if (!state.parsedRows) return;
    setState((s) => ({ ...s, isImporting: true, result: null }));
    try {
      const body = config.buildBody(state.parsedRows, state.dryRun);
      const result = await fetchWithAuth<ImportResult>(config.endpoint, {
        method: "POST",
        body: JSON.stringify(body),
      });
      setState((s) => ({ ...s, isImporting: false, result }));
    } catch (err: any) {
      setState((s) => ({
        ...s,
        isImporting: false,
        result: { created: 0, skipped: 0, errors: [{ reason: err.message || "Request failed" }] },
      }));
    }
  }

  function handleReset() {
    setState({ ...EMPTY_PANEL });
    setExampleOpen(false);
  }

  const previewRows = state.parsedRows?.slice(0, 10) ?? [];
  const extraRows = (state.parsedRows?.length ?? 0) - 10;
  const { columns } = config;
  const importCount = state.parsedRows?.length ?? 0;

  return (
    <div className="space-y-4">
      {/* Source info */}
      <p className="text-sm text-muted-foreground">
        <span className="font-medium">FC source:</span>{" "}
        <code className="text-xs bg-muted px-1 rounded">{config.fcSource}</code>
        {" — "}{config.description}
      </p>

      {/* Example JSON */}
      <Collapsible open={exampleOpen} onOpenChange={setExampleOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="text-muted-foreground gap-1 px-0">
            <ChevronDown className={`h-4 w-4 transition-transform ${exampleOpen ? "rotate-180" : ""}`} />
            Show expected JSON format
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <pre className="text-xs bg-muted rounded-md p-3 overflow-auto max-h-64 font-mono mt-1">
            {config.exampleJson}
          </pre>
        </CollapsibleContent>
      </Collapsible>

      {/* Input area */}
      <div className="space-y-2">
        <Textarea
          value={state.rawText}
          onChange={(e) =>
            setState((s) => ({ ...s, rawText: e.target.value, parsedRows: null, parseError: null, result: null }))
          }
          placeholder={`Paste FC API JSON response here…`}
          className="font-mono text-xs min-h-[14rem] resize-y"
        />
        <div className="flex items-center gap-2">
          <Switch
            id={`dry-run-${tabId}`}
            checked={state.dryRun}
            onCheckedChange={(v) => setState((s) => ({ ...s, dryRun: v }))}
          />
          <Label htmlFor={`dry-run-${tabId}`} className="text-sm cursor-pointer">
            Validate only (don't save)
          </Label>
        </div>
      </div>

      {/* Parse error */}
      {state.parseError && <p className="text-sm text-destructive">{state.parseError}</p>}

      {/* Validate button */}
      <Button variant="outline" onClick={handleValidate} disabled={!state.rawText.trim()}>
        Validate JSON
      </Button>

      {/* Preview table */}
      {state.parsedRows && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {state.parsedRows.length} record{state.parsedRows.length !== 1 ? "s" : ""} parsed
            {extraRows > 0 ? " — showing first 10" : ""}
          </p>
          <div className="border rounded-md overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((c) => (
                    <TableHead key={c.header}>{c.header}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewRows.map((row, i) => (
                  <TableRow key={i}>
                    {columns.map((c) => (
                      <TableCell key={c.header} className="text-xs">
                        {c.accessor(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {extraRows > 0 && <p className="text-xs text-muted-foreground">…and {extraRows} more</p>}

          {/* Import button */}
          <Button onClick={handleImport} disabled={state.isImporting}>
            {state.isImporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Running…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {state.dryRun ? "Dry Run" : "Import"} {importCount} Record{importCount !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Results */}
      {state.result && (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex gap-4 flex-wrap text-sm font-medium">
              {state.result.created !== undefined && <span className="text-green-600">Created: {state.result.created}</span>}
              {state.result.updated !== undefined && <span className="text-blue-600">Updated: {state.result.updated}</span>}
              {state.result.matched !== undefined && <span className="text-green-600">Matched: {state.result.matched}</span>}
              {state.result.skipped !== undefined && <span className="text-yellow-600">Skipped: {state.result.skipped}</span>}
              {state.result.groups_created !== undefined && <span className="text-green-600">Groups created: {state.result.groups_created}</span>}
              {state.result.groups_skipped !== undefined && <span className="text-yellow-600">Groups skipped: {state.result.groups_skipped}</span>}
              {state.result.members_created !== undefined && <span className="text-green-600">Group members added: {state.result.members_created}</span>}
              {state.result.assignments_created !== undefined && <span className="text-green-600">Assignments created: {state.result.assignments_created}</span>}
              {state.result.assignments_skipped !== undefined && <span className="text-yellow-600">Assignments skipped: {state.result.assignments_skipped}</span>}
              {state.result.projects_processed !== undefined && <span className="text-blue-600">Projects processed: {state.result.projects_processed}</span>}
              {state.result.contacts_created !== undefined && <span className="text-blue-600">Contacts created: {state.result.contacts_created}</span>}
              {state.result.filtered_pre_2026 !== undefined && state.result.filtered_pre_2026 > 0 && (
                <span className="text-muted-foreground">Pre-2026 filtered: {state.result.filtered_pre_2026}</span>
              )}
              {state.result.unmatched_projects_count !== undefined && state.result.unmatched_projects_count > 0 && (
                <span className="text-yellow-600">Unmatched projects: {state.result.unmatched_projects_count}</span>
              )}
              <span className="text-destructive">Errors: {state.result.errors.length}</span>
              {state.dryRun && <span className="text-xs text-muted-foreground">(dry run — no data saved)</span>}
            </div>
            {state.result.unmatched_users && state.result.unmatched_users.length > 0 && (
              <div className="text-sm">
                <p className="font-medium text-yellow-700">Unmatched FC users (no CommandHub match):</p>
                <ul className="list-disc list-inside text-xs text-muted-foreground mt-1 space-y-0.5">
                  {state.result.unmatched_users.map((u, i) => <li key={i}>{u}</li>)}
                </ul>
              </div>
            )}
            {state.result.warnings && state.result.warnings.length > 0 && (
              <div className="text-sm">
                <p className="font-medium text-yellow-700">Warnings ({state.result.warnings.length}):</p>
                <div className="max-h-32 overflow-auto mt-1 space-y-0.5">
                  {state.result.warnings.map((w, i) => (
                    <p key={i} className="text-xs text-yellow-700">{w.group}: {w.reason}</p>
                  ))}
                </div>
              </div>
            )}
            {state.result.errors.length > 0 && (
              <div className="max-h-40 overflow-auto space-y-1">
                {state.result.errors.map((e, i) => (
                  <p key={i} className="text-xs text-destructive">
                    {e.group != null ? `Group "${e.group}"` : e.batch != null ? `Batch ${e.batch}` : e.row != null ? `Row ${e.row}` : ""}
                    {(e.group != null || e.batch != null || (e.row != null && e.row > 0)) ? ": " : ""}
                    {e.reason}
                  </p>
                ))}
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={handleReset}>
              Clear / Import More
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---- Single Project Panel ----

interface SingleProjectResult {
  action: "created" | "updated" | "dry_run";
  fc_project_id: string | null;
  project_id?: string;
  title: string;
  client: string;
  account_matched?: boolean;
  template_matched: boolean;
  template_title: string | null;
  would_action?: "create" | "update";
  would_retask?: boolean;
  classification: { project_type: string | null; tax_year: number | null; period_label: string | null };
  tasks_created: number;
  assignees_added: number;
  assignees_in_payload?: number;
  assignees_unmatched?: string[];
}

function SingleProjectPanel() {
  const [rawText, setRawText] = useState("");
  const [sectionsText, setSectionsText] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [forceRetask, setForceRetask] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<SingleProjectResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  function parseAndPreview() {
    setParseError(null); setResult(null); setError(null);
    try { JSON.parse(rawText); } catch { setParseError("Project JSON is invalid — check syntax."); return; }
    if (sectionsText.trim()) {
      try { JSON.parse(sectionsText); } catch { setParseError("Sections JSON is invalid — check syntax."); return; }
    }
  }

  async function handleImport() {
    setIsRunning(true); setResult(null); setError(null);
    let parsed: any, secs: any;
    try { parsed = JSON.parse(rawText); } catch { setError("Project JSON is invalid."); setIsRunning(false); return; }
    if (sectionsText.trim()) {
      try { secs = JSON.parse(sectionsText); } catch { setError("Sections JSON is invalid."); setIsRunning(false); return; }
    }
    const sections = secs ? (secs?.data ?? (Array.isArray(secs) ? secs : [])) : undefined;
    try {
      const data = await fetchWithAuth<SingleProjectResult>("/api/admin/import/project/single", {
        method: "POST",
        body: JSON.stringify({ data: parsed, sections, dry_run: dryRun, force_retask: forceRetask }),
      });
      setResult(data);
    } catch (e: any) {
      setError(e.message || "Request failed");
    } finally {
      setIsRunning(false);
    }
  }

  const isValidJson = (() => { try { JSON.parse(rawText); return true; } catch { return false; } })();

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground space-y-1">
        <p>Paste a single FC project detail JSON — the response from <code className="text-xs bg-muted px-1 rounded">GET /projects/{"{id}"}</code>. Handles both <code className="text-xs bg-muted px-1 rounded">{`{ "data": { ... } }`}</code> and the raw object.</p>
        <p className="text-xs">Auto-classifies project type / year / period. Imports agents as project assignees. Use <strong>Force re-sync tasks</strong> to delete+recreate tasks from template on re-import.</p>
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">Project JSON <span className="text-red-500">*</span></label>
        <p className="text-xs text-muted-foreground">From FC console: <code className="bg-muted px-1 rounded">copy(JSON.stringify(await (await fetch('/api/project/12345678')).json()))</code></p>
        <Textarea
          value={rawText}
          onChange={(e) => { setRawText(e.target.value); setParseError(null); setResult(null); setError(null); }}
          placeholder={`Paste FC project detail JSON here…`}
          className="font-mono text-xs min-h-[10rem] resize-y"
        />
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">Sections JSON <span className="text-muted-foreground text-xs font-normal">(optional — tasks &amp; sub-tasks)</span></label>
        <p className="text-xs text-muted-foreground">From FC console: <code className="bg-muted px-1 rounded">copy(JSON.stringify(await (await fetch('/api/project/12345678/sections')).json()))</code></p>
        <Textarea
          value={sectionsText}
          onChange={(e) => { setSectionsText(e.target.value); setParseError(null); setResult(null); setError(null); }}
          placeholder="Paste FC sections JSON here…"
          className="font-mono text-xs min-h-[10rem] resize-y"
        />
      </div>

      {parseError && <p className="text-sm text-destructive">{parseError}</p>}

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2">
          <Switch id="sp-dry-run" checked={dryRun} onCheckedChange={setDryRun} />
          <Label htmlFor="sp-dry-run" className="text-sm cursor-pointer">Validate only (don't save)</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="sp-retask" checked={forceRetask} onCheckedChange={(v) => setForceRetask(!!v)} />
          <Label htmlFor="sp-retask" className="text-sm cursor-pointer">Force re-sync tasks (delete + recreate from template)</Label>
        </div>
      </div>

      <Button onClick={handleImport} disabled={!rawText.trim() || isRunning}>
        {isRunning ? (
          <><Loader2 className="h-4 w-4 animate-spin mr-2" />Running…</>
        ) : (
          <><RefreshCw className="h-4 w-4 mr-2" />{dryRun ? "Dry Run" : "Import / Re-sync"} Project</>
        )}
      </Button>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {result && (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              {result.action === "dry_run" ? (
                <Badge variant="secondary">Dry Run — would {result.would_action}</Badge>
              ) : result.action === "created" ? (
                <Badge className="bg-green-600 text-white">Created</Badge>
              ) : (
                <Badge className="bg-blue-600 text-white">Updated</Badge>
              )}
              <span className="font-medium text-sm">{result.title}</span>
              <span className="text-xs text-muted-foreground">· {result.client}</span>
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div><span className="text-muted-foreground">FC Project ID:</span> <code className="text-xs">{result.fc_project_id ?? "—"}</code></div>
              <div><span className="text-muted-foreground">Template:</span> {result.template_matched ? <span className="text-green-600">{result.template_title ?? "matched"}</span> : <span className="text-yellow-600">not matched</span>}</div>
              <div><span className="text-muted-foreground">Type:</span> {result.classification.project_type ?? <span className="text-muted-foreground">unclassified</span>}</div>
              <div><span className="text-muted-foreground">Year:</span> {result.classification.tax_year ?? "—"}</div>
              <div><span className="text-muted-foreground">Period:</span> {result.classification.period_label ?? "—"}</div>
              {result.action !== "dry_run" && (
                <>
                  <div><span className="text-muted-foreground">Tasks created:</span> <span className={result.tasks_created > 0 ? "text-green-600 font-medium" : ""}>{result.tasks_created}</span></div>
                  {(result as any).sub_tasks_created > 0 && <div><span className="text-muted-foreground">Sub-tasks created:</span> <span className="text-green-600 font-medium">{(result as any).sub_tasks_created}</span></div>}
                  <div><span className="text-muted-foreground">Assignees added:</span> <span className={result.assignees_added > 0 ? "text-green-600 font-medium" : ""}>{result.assignees_added}</span></div>
                </>
              )}
              {result.action === "dry_run" && result.assignees_in_payload !== undefined && (
                <div><span className="text-muted-foreground">Agents in payload:</span> {result.assignees_in_payload}</div>
              )}
            </div>

            {result.assignees_unmatched && result.assignees_unmatched.length > 0 && (
              <div className="text-sm">
                <p className="text-yellow-700 font-medium">Unmatched agents (no CommandHub user):</p>
                <ul className="list-disc list-inside text-xs text-muted-foreground mt-1">
                  {result.assignees_unmatched.map((u, i) => <li key={i}>{u}</li>)}
                </ul>
              </div>
            )}

            <Button variant="ghost" size="sm" onClick={() => { setRawText(""); setResult(null); setError(null); }}>
              Clear
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---- Task Regen Panel ----

interface TaskRegenResult { processed: number; skipped: number; tasks_created: number; errors: { project_id: string; reason: string }[] }

function TaskRegenPanel() {
  const { user } = useAuth();
  const [dryRun, setDryRun] = useState(true);
  const [limit, setLimit] = useState<string>("");
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<TaskRegenResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleRun() {
    setIsRunning(true); setResult(null); setError(null);
    try {
      const body: Record<string, unknown> = { dry_run: dryRun };
      const lim = parseInt(limit);
      if (!isNaN(lim) && lim > 0) body.limit = lim;
      const data = await fetchWithAuth<TaskRegenResult>("/api/admin/import/project-tasks-regen", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setResult(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsRunning(false);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Re-generates project tasks for all imported projects from their linked templates.
        Now captures <strong>predecessor_task_id</strong> (task dependencies) and sets{" "}
        <strong>is_blocked = true</strong> for tasks that must wait on a predecessor.
        <br /><span className="text-yellow-700 font-medium">⚠ Deletes and recreates all project tasks. Run templates import first.</span>
      </p>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Checkbox id="regen-dry-run" checked={dryRun} onCheckedChange={(v) => setDryRun(!!v)} />
          <Label htmlFor="regen-dry-run" className="text-sm cursor-pointer">Validate only (don't save)</Label>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm">Limit (optional):</Label>
          <input
            type="number" min="1" placeholder="all projects"
            value={limit} onChange={(e) => setLimit(e.target.value)}
            className="border rounded px-2 py-1 text-sm w-32"
          />
        </div>
      </div>
      <Button onClick={handleRun} disabled={isRunning}>
        {isRunning ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Running…</> : <><Upload className="h-4 w-4 mr-2" />{dryRun ? "Dry Run" : "Regenerate Tasks"}</>}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {result && (
        <Card><CardContent className="pt-4 space-y-3">
          <div className="flex gap-4 flex-wrap text-sm font-medium">
            <span className="text-green-600">Projects processed: {result.processed}</span>
            <span className="text-yellow-600">Skipped (no template): {result.skipped}</span>
            <span className="text-blue-600">Tasks created: {result.tasks_created}</span>
            <span className="text-destructive">Errors: {result.errors.length}</span>
            {dryRun && <span className="text-xs text-muted-foreground">(dry run — no data saved)</span>}
          </div>
          {result.errors.length > 0 && (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {result.errors.map((e, i) => (
                <p key={i} className="text-xs text-destructive">Project {e.project_id}: {e.reason}</p>
              ))}
            </div>
          )}
        </CardContent></Card>
      )}
    </div>
  );
}

// ---- Project Users Auto-Populate Panel ----

interface ProjectUsersResult { projects_processed: number; assignments_created: number; assignments_skipped: number; errors: { project_id: string; reason: string }[] }

function ProjectUsersPanel() {
  const [dryRun, setDryRun] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<ProjectUsersResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleRun() {
    setIsRunning(true); setResult(null); setError(null);
    try {
      const data = await fetchWithAuth<ProjectUsersResult>("/api/admin/import/project-users-from-members", {
        method: "POST",
        body: JSON.stringify({ dry_run: dryRun }),
      });
      setResult(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsRunning(false);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Populates project team assignments for all imported projects by copying each account's
        members (<strong>account_members</strong>) into <strong>project_users</strong>.
        This gives every project the right staff without needing per-project FC API data.
        <br />Safe to re-run — skips assignments that already exist.
      </p>
      <div className="flex items-center gap-2">
        <Checkbox id="pu-dry-run" checked={dryRun} onCheckedChange={(v) => setDryRun(!!v)} />
        <Label htmlFor="pu-dry-run" className="text-sm cursor-pointer">Validate only (don't save)</Label>
      </div>
      <Button onClick={handleRun} disabled={isRunning}>
        {isRunning ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Running…</> : <><Upload className="h-4 w-4 mr-2" />{dryRun ? "Dry Run" : "Populate Project Users"}</>}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {result && (
        <Card><CardContent className="pt-4 space-y-2">
          <div className="flex gap-4 flex-wrap text-sm font-medium">
            <span className="text-blue-600">Projects with members: {result.projects_processed}</span>
            <span className="text-green-600">Assignments created: {result.assignments_created}</span>
            <span className="text-yellow-600">Already assigned (skipped): {result.assignments_skipped}</span>
            <span className="text-destructive">Errors: {result.errors.length}</span>
            {dryRun && <span className="text-xs text-muted-foreground">(dry run — no data saved)</span>}
          </div>
          {result.errors.length > 0 && (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {result.errors.map((e, i) => <p key={i} className="text-xs text-destructive">Project {e.project_id}: {e.reason}</p>)}
            </div>
          )}
        </CardContent></Card>
      )}
    </div>
  );
}

// ---- About Fields Panel ----

function AboutFieldsPanel() {
  const [state, setState] = useState<AboutFieldsState>({ dryRun: true, isRunning: false, result: null });

  async function handleRun() {
    setState(s => ({ ...s, isRunning: true, result: null }));
    try {
      const result = await fetchWithAuth<AboutFieldsResult>("/api/admin/import/about-fields", {
        method: "POST",
        body: JSON.stringify({ dry_run: state.dryRun }),
      });
      setState(s => ({ ...s, isRunning: false, result }));
    } catch (err: any) {
      setState(s => ({
        ...s,
        isRunning: false,
        result: {
          updated: 0,
          skipped: 0,
          errors: [{ account_id: "", fc_client_id: "", reason: err.message || "Request failed" }],
        },
      }));
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        <span className="font-medium">FC source:</span>{" "}
        <code className="text-xs bg-muted px-1 rounded">GET /clients/{"{id}"}/about-fields</code>
        {" — "}Fetches entity type, EIN, address, SmartVault URL, dates, and more for every account
        with an <code className="text-xs bg-muted px-1 rounded">fc_client_id</code>.
        No JSON to paste — the server fetches directly using FC_API_TOKEN.
        May take 20–30 seconds for large client lists.
      </p>

      <div className="flex items-center gap-2">
        <Switch
          id="af-dry-run"
          checked={state.dryRun}
          onCheckedChange={v => setState(s => ({ ...s, dryRun: v }))}
          disabled={state.isRunning}
        />
        <Label htmlFor="af-dry-run" className="text-sm cursor-pointer">Validate only (don't save)</Label>
      </div>

      <Button onClick={handleRun} disabled={state.isRunning}>
        {state.isRunning
          ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Fetching from FC…</>
          : <><Upload className="h-4 w-4 mr-2" />{state.dryRun ? "Dry Run" : "Fetch & Update"} About Fields</>}
      </Button>

      {state.isRunning && (
        <p className="text-xs text-muted-foreground">
          Fetching per-client data from Financial Cents. This may take 20–30 seconds…
        </p>
      )}

      {state.result && (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex gap-4 flex-wrap text-sm font-medium">
              <span className="text-green-600">Updated: {state.result.updated}</span>
              <span className="text-yellow-600">Skipped (no data): {state.result.skipped}</span>
              <span className="text-destructive">Errors: {state.result.errors.length}</span>
              {state.dryRun && (
                <span className="text-xs text-muted-foreground">(dry run — no data saved)</span>
              )}
            </div>
            {state.result.errors.length > 0 && (
              <div className="max-h-40 overflow-auto space-y-1">
                {state.result.errors.map((e, i) => (
                  <p key={i} className="text-xs text-destructive">
                    {e.account_id ? `Account ${e.account_id}` : ""}
                    {e.fc_client_id ? ` (FC ${e.fc_client_id})` : ""}
                    {(e.account_id || e.fc_client_id) ? ": " : ""}
                    {e.reason}
                  </p>
                ))}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setState({ dryRun: true, isRunning: false, result: null })}
            >
              Clear
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---- Maintenance Panel ----

function MaintenanceAction({ title, description, endpoint }: { title: string; description: string; endpoint: string }) {
  const [result, setResult] = useState<{ updated: number; message: string; clients?: string[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const run = async (dryRun: boolean) => {
    setLoading(true);
    setResult(null);
    try {
      const data = await fetchWithAuth<{ updated: number; message: string }>(
        endpoint,
        { method: "POST", body: JSON.stringify({ dry_run: dryRun }) }
      );
      setResult(data);
      if (!dryRun) toast({ title: data.message });
    } catch (e: any) {
      toast({ title: e?.message || "Failed", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2 pb-4 border-b last:border-0 last:pb-0">
      <div>
        <p className="font-semibold text-sm">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => run(true)} disabled={loading}>
          {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
          Preview
        </Button>
        <Button size="sm" variant="destructive" onClick={() => run(false)} disabled={loading}>
          {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
          Apply
        </Button>
      </div>
      {result && (
        <div className="space-y-1">
          <p className={`text-sm font-medium ${result.updated > 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
            {result.message}
          </p>
          {result.clients && result.clients.length > 0 && (
            <ul className="text-xs text-muted-foreground space-y-0.5 pl-3 border-l-2 border-muted">
              {result.clients.map((c) => <li key={c}>{c}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function MaintenancePanel() {
  return (
    <Card>
      <CardContent className="pt-5 space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Maintenance Actions</p>
        <MaintenanceAction
          title="Close open projects for inactive clients"
          description="Finds all open/in-progress projects belonging to inactive clients and marks them closed. Run Preview first."
          endpoint="/api/admin/close-inactive-client-projects"
        />
        <MaintenanceAction
          title="Mark tasks done for closed/completed projects"
          description="Finds tasks inside closed or completed projects that are still open and marks them done."
          endpoint="/api/admin/backfill-closed-tasks"
        />
      </CardContent>
    </Card>
  );
}

// ---- Project Sections Panel (FC tasks + sub-tasks import) ----

interface ProjectSectionsResult {
  projects_processed: number;
  projects_skipped: number;
  tasks_created: number;
  sub_tasks_created: number;
  dry_run: boolean;
  force_replace: boolean;
  errors: { project: string; reason: string }[];
}

function ProjectSectionsPanel() {
  const [rawText, setRawText] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [forceReplace, setForceReplace] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<ProjectSectionsResult | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ projects: number; tasks: number; subTasks: number } | null>(null);

  function handleValidate() {
    setParseError(null); setResult(null); setApiError(null); setPreview(null);
    let parsed: any;
    try { parsed = JSON.parse(rawText); } catch {
      setParseError("Invalid JSON — check syntax."); return;
    }
    const entries: any[] | null = Array.isArray(parsed) ? parsed : parsed?.data ? [].concat(parsed.data) : null;
    if (!entries || entries.length === 0) {
      setParseError('Expected an array of { fc_project_id, sections } entries from the fetch script.'); return;
    }
    // Quick preview counts
    let totalTasks = 0, totalSubs = 0;
    for (const e of entries) {
      for (const sec of e.sections || []) {
        totalTasks += (sec.tasks || []).length;
        for (const t of sec.tasks || []) totalSubs += (t.sub_tasks || []).length;
      }
    }
    setPreview({ projects: entries.length, tasks: totalTasks, subTasks: totalSubs });
  }

  async function handleImport() {
    setIsRunning(true); setResult(null); setApiError(null);
    let parsed: any;
    try { parsed = JSON.parse(rawText); } catch { setApiError("Invalid JSON."); setIsRunning(false); return; }
    const entries: any[] = Array.isArray(parsed) ? parsed : [].concat(parsed?.data ?? []);
    try {
      const data = await fetchWithAuth<ProjectSectionsResult>("/api/admin/import/project-sections", {
        method: "POST",
        body: JSON.stringify({ data: entries, dry_run: dryRun, force_replace: forceReplace }),
      });
      setResult(data);
    } catch (e: any) {
      setApiError(e.message);
    } finally {
      setIsRunning(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground space-y-1">
        <p>
          Imports actual per-project tasks and sub-tasks from Financial Cents using the
          {" "}<code className="text-xs bg-muted px-1 rounded">GET /projects/&#123;id&#125;/sections</code>{" "}
          endpoint (visible in browser devtools when viewing a project in FC).
        </p>
        <p>
          <strong>Step 1 — Fetch the data:</strong> Run the fetch script from the repo root:
        </p>
        <pre className="text-xs bg-muted rounded p-2 overflow-auto">
          node scripts/fetch-fc-project-sections.mjs --limit 50{"\n"}
          # or a single project:{"\n"}
          node scripts/fetch-fc-project-sections.mjs --id 10718488{"\n"}
          # open projects only (faster for large firms):{"\n"}
          node scripts/fetch-fc-project-sections.mjs --open-only
        </pre>
        <p>
          <strong>Step 2 — Paste the output</strong>{" "}
          (<code className="text-xs bg-muted px-1 rounded">scripts/fc-project-sections.json</code>) below.
        </p>
        <p className="text-yellow-700 font-medium text-xs">
          ⚠ <strong>Force Replace</strong> deletes all existing tasks for each project before inserting FC data.
          Only enable this if you want to overwrite manually-created tasks with the FC ground truth.
        </p>
      </div>

      <Textarea
        className="font-mono text-xs min-h-[160px]"
        placeholder='Paste fc-project-sections.json here — array of { fc_project_id, sections: [...] }'
        value={rawText}
        onChange={(e) => { setRawText(e.target.value); setPreview(null); setResult(null); setParseError(null); }}
      />

      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Checkbox id="ps-dry-run" checked={dryRun} onCheckedChange={(v) => setDryRun(!!v)} />
          <Label htmlFor="ps-dry-run" className="text-sm cursor-pointer">Dry run (count only, no changes)</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="ps-force" checked={forceReplace} onCheckedChange={(v) => setForceReplace(!!v)} />
          <Label htmlFor="ps-force" className="text-sm cursor-pointer text-yellow-700">Force Replace (delete existing tasks first)</Label>
        </div>
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <Button variant="outline" onClick={handleValidate} disabled={!rawText.trim() || isRunning}>
          Validate & Preview
        </Button>
        <Button onClick={handleImport} disabled={!rawText.trim() || isRunning}>
          {isRunning
            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Importing…</>
            : <><Upload className="h-4 w-4 mr-2" />{dryRun ? "Dry Run" : "Import"} Tasks</>}
        </Button>
      </div>

      {parseError && <p className="text-sm text-destructive">{parseError}</p>}

      {preview && !result && (
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm font-medium mb-1">Preview (not yet saved):</p>
            <div className="flex gap-4 flex-wrap text-sm">
              <span className="text-blue-600">Projects: {preview.projects}</span>
              <span className="text-blue-600">Tasks: {preview.tasks}</span>
              <span className="text-green-600">Sub-tasks: {preview.subTasks}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {apiError && <p className="text-sm text-destructive">{apiError}</p>}

      {result && (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex gap-4 flex-wrap text-sm font-medium">
              <span className="text-green-600">Projects processed: {result.projects_processed}</span>
              {result.projects_skipped > 0 && <span className="text-yellow-600">Skipped (not found): {result.projects_skipped}</span>}
              <span className="text-blue-600">Tasks created: {result.tasks_created}</span>
              <span className="text-green-600">Sub-tasks created: {result.sub_tasks_created}</span>
              <span className="text-destructive">Errors: {result.errors.length}</span>
              {result.dry_run && <span className="text-xs text-muted-foreground">(dry run — no data saved)</span>}
              {result.force_replace && !result.dry_run && <span className="text-xs text-yellow-700">(existing tasks replaced)</span>}
            </div>
            {result.errors.length > 0 && (
              <div className="max-h-40 overflow-auto space-y-1">
                {result.errors.map((e, i) => (
                  <p key={i} className="text-xs text-destructive">{e.project}: {e.reason}</p>
                ))}
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={() => { setRawText(""); setResult(null); setPreview(null); }}>
              Clear / Import More
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---- Projects Tab (two modes: Single + Bulk) ----

function ProjectsTabContent() {
  const [mode, setMode] = useState<"single" | "bulk">("single");
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-b pb-3">
        <Button
          size="sm"
          variant={mode === "single" ? "default" : "outline"}
          onClick={() => setMode("single")}
        >
          Single Project
        </Button>
        <Button
          size="sm"
          variant={mode === "bulk" ? "default" : "outline"}
          onClick={() => setMode("bulk")}
        >
          Bulk Import
        </Button>
        {mode === "single" && (
          <span className="text-xs text-muted-foreground ml-2">Import or re-sync one project by FC project detail JSON</span>
        )}
        {mode === "bulk" && (
          <span className="text-xs text-muted-foreground ml-2">Paste full FC GET /projects response to import many at once</span>
        )}
      </div>
      {mode === "single" ? <SingleProjectPanel /> : <ImportPanel tabId="projects" />}
    </div>
  );
}

// ---- Page ----

export default function AdminImport() {
  const { user } = useAuth();

  if (user?.role !== "admin") {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-muted-foreground">
          <ShieldAlert className="h-10 w-10" />
          <p className="text-lg font-medium">Admin access required</p>
          <p className="text-sm">This page is only available to administrators.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">FC Data Import</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Financial Cents migration tool. Paste raw FC API JSON responses to import clients, templates,
            projects, notes, and time history. Always dry-run first to check for errors.
          </p>
          <p className="text-muted-foreground text-xs mt-1">
            Import order: <strong>Accounts → Templates → Projects → Client Notes → Time Activities → Users → Project Roles → Project Assignments → Task Regen</strong>
          </p>
        </div>

        <Tabs defaultValue="accounts">
          <TabsList className="flex-wrap h-auto gap-1">
            <TabsTrigger value="accounts">Accounts</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
            <TabsTrigger value="client-notes">Client Notes</TabsTrigger>
            <TabsTrigger value="time-activities">Time Activities</TabsTrigger>
            <TabsTrigger value="about-fields">About Fields</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="project-roles">Project Roles</TabsTrigger>
            <TabsTrigger value="project-assignments">Assignments</TabsTrigger>
            <TabsTrigger value="client-groups">Client Groups</TabsTrigger>
            <TabsTrigger value="task-regen">Task Regen</TabsTrigger>
            <TabsTrigger value="project-users">Project Users</TabsTrigger>
            <TabsTrigger value="project-sections">Project Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="accounts" className="mt-4">
            <ImportPanel tabId="accounts" />
          </TabsContent>
          <TabsContent value="templates" className="mt-4">
            <ImportPanel tabId="templates" />
          </TabsContent>
          <TabsContent value="projects" className="mt-4">
            <ProjectsTabContent />
          </TabsContent>
          <TabsContent value="client-notes" className="mt-4">
            <ImportPanel tabId="client-notes" />
          </TabsContent>
          <TabsContent value="time-activities" className="mt-4">
            <ImportPanel tabId="time-activities" />
          </TabsContent>
          <TabsContent value="about-fields" className="mt-4">
            <AboutFieldsPanel />
          </TabsContent>
          <TabsContent value="users" className="mt-4">
            <ImportPanel tabId="users" />
          </TabsContent>
          <TabsContent value="project-roles" className="mt-4">
            <ImportPanel tabId="project-roles" />
          </TabsContent>
          <TabsContent value="project-assignments" className="mt-4">
            <ImportPanel tabId="project-assignments" />
          </TabsContent>
          <TabsContent value="client-groups" className="mt-4">
            <ImportPanel tabId="client-groups" />
          </TabsContent>
          <TabsContent value="task-regen" className="mt-4">
            <TaskRegenPanel />
          </TabsContent>
          <TabsContent value="project-users" className="mt-4">
            <ProjectUsersPanel />
          </TabsContent>
          <TabsContent value="project-sections" className="mt-4">
            <ProjectSectionsPanel />
          </TabsContent>
        </Tabs>

        {/* Maintenance Actions */}
        <MaintenancePanel />
      </div>
    </Layout>
  );
}
